import { formatDate } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, of, take } from 'rxjs';
import { ChassisTripsModel } from 'src/app/models/ChassisResponse';
import { ChassisTimeTableModel } from 'src/app/models/ChassisTimeTable';
import { DocksModel } from 'src/app/models/DocksModel';
import { ChassisService } from 'src/app/services/chassis/chassis.service';
import { DateUtilsService } from 'src/app/shared/utils/date-utils.service';

@Component({
  selector: 'app-trip-details',
  templateUrl: './trip-details.component.html',
  styleUrls: ['./trip-details.component.css']
})
export class TripDetailsComponent implements OnInit {
  @Input() isViewOnly = false

  dateInterval: any
  updateInterval: any

  tripDBData: ChassisTripsModel | undefined
  chassisDocks: DocksModel[] = [];
  dockId!: number;
  dockData?: DocksModel | any = undefined;
  activeDock: boolean = false;

  isExtraTrip: boolean = false;
  isLoading: boolean = false;
  pausePooling: boolean = false;
  showIntegrationForm: boolean = false;

  bgColor: string = '#FFF';

  takt: string = "";
  definedArrival?: string | null;
  definedDeparture?: string | null;
  remainingRealTimeFormatted?: string | null;
  processDescription: string | null = null;

  currentTimeInMs = Date.now();

  constructor(
    private readonly datesService: DateUtilsService,
    private readonly chassisService: ChassisService,
    private readonly route: ActivatedRoute,
    private readonly router: Router) { }

  ngOnInit(): void {

    this.route.paramMap.subscribe(params => {
      const newDockId = Number(params.get('id'))

      if (this.dockId !== newDockId) {
        this.dockId = newDockId
        this.loadDockData(true)
      }
      this.isViewOnly = this.router.url.includes("/view")
    })

    this.updateData();

    setInterval(() => {
      this.defaultTimer();
    }, 1000)
    this.dateInterval = setInterval(() => {
      if (!this.pausePooling) {
        this.updateData();
      }
    }, 5000);
  }

  private loadDockData(showLoading: boolean = false) {
    if (showLoading) {
      this.isLoading = true
    }

    this.pausePooling = true;

    this.chassisService.getChassisDocksDataView().pipe
      (take(1),
        catchError(() => {
          this.pausePooling = false;
          this.isLoading = false;
          return of([])
        })
      ).subscribe(
        (response) => {
          this.chassisDocks = response.map(d => ({
            ...d,
            status: new Date(d.status + 'Z')
          }));
        }
      )

    this.chassisService.getActiveTripsView().pipe
      (take(1),
        catchError(() => {
          this.pausePooling = false;
          this.isLoading = false;
          return of([]);
        })
      ).subscribe(
        (response) => {
          this.tripDBData = response.find(t => t.dockId === this.dockId)
          this.dockData = this.chassisDocks.find(dock => dock.id === this.dockId);
          this.clearDisplayedData()

          this.pausePooling = false;
          this.isLoading = false;
          this.activeDock = true;
        }
      )
  }

  private updateData(): void {

    this.chassisService.getChassisDocksDataView().pipe
      (take(1),
        catchError(() => {
          this.pausePooling = false;
          this.isLoading = false;
          return of([])
        })
      ).subscribe(
        (response) => {
          this.chassisDocks = response.map((d: DocksModel) => ({
            ...d,
            status: new Date(d.status + 'Z')
          }));
        }
      )

    this.dockData = this.chassisDocks.find(dock => dock.id === this.dockId);

    if (this.shouldUpdateDockInfo()) {
      console.log("Updating dock info...");
      this.chassisService.getActiveTripsView().pipe
        (take(1),
          catchError(() => {
            return of([]);
          })
        ).subscribe(
          (response) => {
            this.tripDBData = response.find(t => t.dockId === this.dockId)
          }
        )

      if (this.tripDBData?.trip != 0) {
        this.checkData()
      } else {
        this.defaultTimer();
      }
    } else {
      console.log("Resetting dock info...");
      this.resetTimer();
    }
  }

  private defaultTimer() {

    if (!this.shouldUpdateDockInfo()) {
      return;
    }
    this.currentTimeInMs = Date.now()

    const statusTime = Number(this.dockData?.status);
    const standartTakt: number = 60 * 60 * 1000;

    const remainingTime: number = standartTakt + statusTime - this.currentTimeInMs;

    this.takt = this.datesService.formatTime(remainingTime);
    this.taktBackgroundColor(remainingTime, standartTakt);
    this.activeDock = true;
  }

  private resetTimer() {
    const loadingTimer = "--:--:--";
    this.activeDock = false;
    this.bgColor = "#FFF";
    this.takt = loadingTimer;
    this.tripDBData = undefined;
    //this.dockData!.truck = 0;
  }

  private clearDisplayedData(): void {
    this.tripDBData = undefined;
    this.dockData = undefined;
    this.definedArrival = null;
    this.definedDeparture = null;
    this.remainingRealTimeFormatted = null;
    this.takt = '--:--:--';
    this.bgColor = '#FFF';
    this.activeDock = false;
  }

  private shouldUpdateDockInfo() {
    console.log("Truck:", this.dockData?.truck, "Active:", this.dockData?.active);
    console.log("Should update:", this.dockData?.truck == 1 && this.dockData.active == 1);
    return this.dockData?.truck == 1 && this.dockData.active == 1;
  }

  checkData() {
    this.currentTimeInMs = Date.now()

    const currentTime = this.datesService.convertTimeStringToMs(
      this.datesService.extractTimeFromTimeStamp(this.currentTimeInMs));
    const statusTime = this.datesService.convertTimeStringToMs(
      this.datesService.formatTimeToJsDate(this.dockData?.status));

    this.chassisService.getTripByIdView(this.tripDBData!.trip!)
      .pipe(
        take(1),
        catchError(() => of({} as ChassisTimeTableModel))
      )
      .subscribe((response: ChassisTimeTableModel) => {

        const isExtra = response.extra != null
        let taktTime: number;
        let delta: number;

        this.isExtraTrip = isExtra
        this.definedArrival = null
        this.processDescription = response.processDescription


        if (isExtra) {
          this.definedArrival = null
          this.definedDeparture = null
          taktTime = this.datesService.convertTimeStringToMs(response.extra!);
        }
        else {
          this.definedArrival = response.arrivalTimeSLA
          this.definedDeparture = response.departureTimeSLA

          taktTime = this.datesService.convertTimeStringToMs(
            this.datesService.calculateDifference(this.definedArrival!, this.definedDeparture!))

          this.remainingRealTimeFormatted = this.datesService.calculateDeltaFormatted(this.definedDeparture!)
        }

        delta = this.datesService.calculateDelta(statusTime, currentTime, taktTime)
        this.takt = formatDate(Math.abs(delta), 'HH:mm:ss', 'en-US', 'UTC')
        this.taktBackgroundColor(delta, taktTime)
      });
  }

  //* -------------------------------------------- COLORAÇÃO TELA DE FUNDO  -------------------------------------------- *//

  private taktBackgroundColor(remainingTime: number, standartTakt: number) {
    if (remainingTime < 0) {
      this.bgColor = '#FF2340';
      this.takt = '-' + this.takt;
    } else if (remainingTime < (standartTakt) / 3) {
      this.bgColor = '#F1C21B';
    } else {
      this.bgColor = '#3CB371';
    }
  }

  getArrivalColor(): string {
    if (!this.dockData?.status || !this.definedArrival) return "#FFF"

    const realDate = this.datesService
      .formatTimeToJsDate(new Date(this.dockData.status).toString())
    const plannedDate = this.datesService
      .formatTimeToJsDate(this.datesService.convertTimeStringToValidDate(this.definedArrival)!.toString())

    if (plannedDate) {
      return realDate > plannedDate ? "#FF2340" : "#3CB371";
    } return "#FFF"

    // const statusDate = new Date(this.dockData.status);
    // const plannedDate = this.datesService.convertTimeStringToValidDate(this.definedArrival!);

    // if (!plannedDate) return "#FFF";

    // const statusTimeInMs = this.datesService.getTimeOfDayInMs(statusDate);
    // const plannedTimeInMs = this.datesService.getTimeOfDayInMs(plannedDate);

    // return statusTimeInMs > plannedTimeInMs ? "#FF2340" : "#3CB371";


  }

  getDepartureColor(): string {
    if (!this.dockData?.status || !this.definedDeparture) return "#FFF"

    const now = new Date();
    const plannedDeparture = this.datesService.convertTimeStringToValidDate(this.definedDeparture!);

    const remaining = plannedDeparture!.getTime() - now.getTime()
    return remaining < 0 ? "#FF2340" : "#3CB371";
  }

  //* -------------------------------------------- TOGGLE -------------------------------------------- *//

  visibilityClick() {
    this.showIntegrationForm = true
  }

  toggleIntegrationForm() {
    this.showIntegrationForm = !this.showIntegrationForm
  }

  ngOnDestroy(): void {
    clearInterval(this.dateInterval);
  }

}
